<script lang="ts">
  import DynamicButton from "../+shared/Button/DynamicButton/DynamicButton.svelte";
  import OVIcon from "../+shared/OVIcon/OVIcon.svelte";
  import VersionPromptModal from "../+shared/Modal/VersionPromptModal.svelte";
  import { Spinner, Edit } from "$components/+shared/OVIcon";
  import { fade } from "svelte/transition";
  import { alerts } from "$stores/alert";
  import { tooltip } from "app/actions/tooltip";
  import { DocumentService } from "$service/documentService";
  import { DocumentTypeEnum } from "app/models/documents";
  import { getMediaQuery } from "app/utils/mediaQuery";
  import type { YdocStore } from "$components/editor";

  export let readOnly: boolean;
  export let isSaving: boolean;
  export let docType: DocumentTypeEnum = DocumentTypeEnum.Document;
  export let ydoc: YdocStore | null;
  export let versionHistoryFlag: boolean;
  export let sidePanel: any = undefined;
  export let isPublished: boolean;
  export let documentService: DocumentService;

  let isSavingDraft = false;
  let showPublishComment = false;

  const isDesktop = getMediaQuery("desktop");

  const docTypeLabel: Record<DocumentTypeEnum, string> = {
    [DocumentTypeEnum.Document]: "doc",
    [DocumentTypeEnum.Snippet]: "snippet",
    [DocumentTypeEnum.CustomSpace]: "",
  };

  const editLabel = `Edit ${docTypeLabel[docType]}`;
</script>

{#if readOnly && !isSaving}
  <DynamicButton
    variant="primary"
    iconRight={Edit}
    a11yLabel={editLabel}
    on:click={() => {
      if (sidePanel && $isDesktop) {
        sidePanel.show();
      }
      readOnly = false;
    }}
    label={editLabel}
  />
{:else if isSaving}
  <span in:fade>
    <OVIcon icon={Spinner} class="animate-spin text-gray-500 dark:text-white" />
  </span>
{:else}
  {#if docType === DocumentTypeEnum.Document || docType === DocumentTypeEnum.Snippet}
    <span
      class="text-xs font-medium text-gray-400 dark:text-gray-400/60"
      title="Draft synced successfully"
      use:tooltip>Draft saved</span
    >
    <DynamicButton
      variant="secondary"
      a11yLabel="Close edit mode"
      on:click={async () => {
        isSavingDraft = true;
        await documentService.saveSpace(true, "");
        isSavingDraft = false;
        readOnly = true;
      }}
      label={isSavingDraft ? "Saving draft..." : "Leave draft"}
    />
  {/if}

  {#if [DocumentTypeEnum.Document, DocumentTypeEnum.Snippet].includes(docType) && versionHistoryFlag}
    <DynamicButton
      variant="community"
      a11yLabel="Create new version"
      on:click={() => {
        if (!ydoc) return;
        showPublishComment = true;
      }}
      label={isSaving ? "Updating..." : "Create new version"}
    />
  {:else}
    <DynamicButton
      variant="community"
      a11yLabel="Publish changes"
      on:click={async () => {
        if (!ydoc) return;
        if (!ydoc.hasChanges()) {
          alerts.error("No changes detected", {
            description: "Please make changes to the draft before publishing.",
          });
          return;
        }
        isSaving = true;
        await documentService.saveSpace(false, "");
        isSaving = false;
        readOnly = true;
      }}
      label={isSaving ? "Updating..." : "Update"}
    />
  {/if}
{/if}

<VersionPromptModal
  bind:open={showPublishComment}
  title="Create new version"
  confirmLabel="Create version"
  submessage={isPublished
    ? "Note: the new version will be automatically published. This can be reverted via the document version history."
    : ""}
  on:submit={async (ev) => {
    isSaving = true;
    await documentService.saveSpace(false, ev.detail.value);
    isSaving = false;
    showPublishComment = false;
    readOnly = true;
  }}
/>
